import React, { useState, useEffect } from "react";

// ── Styles ─────────────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(2px)",
    zIndex: 1060,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headTitle: { fontSize: 15, fontWeight: 700, color: "#1a1d23" },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
    color: "#6b7280",
    padding: "0 4px",
  },
  body: { padding: "1.25rem", overflowY: "auto" },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  infoBanner: {
    fontSize: 13,
    color: "#1E40AF",
    background: "#EFF6FF",
    border: "1px solid #BFDBFE",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1.25rem",
    lineHeight: 1.55,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1.25rem",
  },
  fieldGroup: { marginBottom: "1rem" },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 5,
  },
  hint: { fontSize: 11, color: "#9ca3af", marginTop: 4 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    outline: "none",
    background: "#fff",
  },
  select: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    outline: "none",
    background: "#fff",
  },
  divider: {
    borderTop: "1px solid #f3f4f6",
    margin: "1rem 0",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: "0.75rem",
  },
  btn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "7px 16px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "7px 18px",
    borderRadius: 7,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 6,
  },
};

// ── Component ──────────────────────────────────────────────────────────────────
function ConvertToBuildingModal({ show, property, onConvert, onClose, loading, error }) {
  const [buildingName, setBuildingName] = useState("");
  const [unitNumber, setUnitNumber] = useState("Unit 1");
  const [buildingType, setBuildingType] = useState("other");

  // Pre-fill from property whenever modal opens
  useEffect(() => {
    if (show && property) {
      setBuildingName(property.title || "");
      setUnitNumber("Unit 1");
      setBuildingType("other");
    }
  }, [show, property]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConvert({ buildingName, unitNumber, buildingType });
  };

  const typeLabelMap = {
    one_time_rental: "One-time rental",
    property_management: "Property management",
  };

  return (
    <div
      style={ms.overlay}
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
    >
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal}>

        {/* Header */}
        <div style={ms.head}>
          <span style={ms.headTitle}>Convert to multi-unit building</span>
          <button style={ms.closeBtn} onClick={onClose} disabled={loading}>×</button>
        </div>

        {/* Body */}
        <div style={ms.body}>
          <div style={ms.infoBanner}>
            <strong>What this does:</strong> A new building record will be created using this
            property's address. The current property (
            <em>{property?.title || "this property"}</em>,{" "}
            {typeLabelMap[property?.type] || property?.type}) will become{" "}
            <strong>the first unit</strong> of that building — all its data, tenant links, and
            financials stay intact. You can then add more units to the building.
          </div>

          {error && <div style={ms.errorBanner}>{error}</div>}

          <form onSubmit={handleSubmit} id="convert-to-building-form">

            {/* Building details */}
            <div style={ms.sectionLabel}>New building</div>

            <div style={ms.fieldGroup}>
              <label style={ms.label}>Building name *</label>
              <input
                style={ms.input}
                type="text"
                required
                value={buildingName}
                onChange={(e) => setBuildingName(e.target.value)}
                placeholder="e.g. 12 Cascade Road Building"
                disabled={loading}
              />
              <div style={ms.hint}>
                This will be the name of the new building. The address is inherited from the current property.
              </div>
            </div>

            <div style={ms.fieldGroup}>
              <label style={ms.label}>Building type</label>
              <select
                style={ms.select}
                value={buildingType}
                onChange={(e) => setBuildingType(e.target.value)}
                disabled={loading}
              >
                <option value="apartment_complex">Apartment complex</option>
                <option value="commercial">Commercial</option>
                <option value="mixed_use">Mixed use</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={ms.divider} />

            {/* Unit details */}
            <div style={ms.sectionLabel}>Current property as a unit</div>

            <div style={ms.fieldGroup}>
              <label style={ms.label}>Unit label for this property *</label>
              <input
                style={ms.input}
                type="text"
                required
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
                placeholder="e.g. Unit 1, Apt A, Ground Floor"
                disabled={loading}
              />
              <div style={ms.hint}>
                How this property will be identified as a unit within the building.
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div style={ms.foot}>
          <button style={ms.btn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            style={ms.btnPrimary}
            form="convert-to-building-form"
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !buildingName.trim() || !unitNumber.trim()}
          >
            {loading ? (
              <><span style={ms.spinner} />Converting…</>
            ) : (
              "Create building"
            )}
          </button>
        </div>

      </div>
    </div>
  );
}

export default ConvertToBuildingModal;
